import { defineStore } from 'pinia';
import * as constants from '../constants';

// eslint-disable-next-line import/prefer-default-export
export const useAppStore = defineStore('app', {
    id: 'app',

    state: () => ({
        // Widget configuration options.
        options: {},

        currentStep: constants.STEP_LOADING,

        isMobile: false,

        isFullscreen: false,

        isBusy: true,
        isQuietBusy: false,

        isLoading: true,

        linkCopied: false,

        selfDestruct: false,

        showGroupOverlay: false,

        nestedPanels: [],

        openModalCount: 0,

        shouldShowResetModal: false,

        shouldShowSummary: false,

        isPrintMode: false,

        shouldShowViewAll: false,

        translations: null,

        toasts: [],
    }),

    getters: {
        hasOpenNestedPanel: (state) => state.nestedPanels.length > 0,

        hasOpenModal: (state) => state.openModalCount > 0,

        activeNestedPanel: (state) => state.nestedPanels[state.nestedPanels.length - 1] || null,
    },

    actions: {
        scrollPanelToTop() {
            const top = document.getElementById('cz3__step-options--scroll-to-target');
            top.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        },
        /**
         * Save options.
         */
        setOptions(options) {
            this.options = options;
        },

        /**
         * Save translations.
         */
        setTranslations(translations) {
            this.translations = translations;
        },

        /**
         * Log an event.
         */
        log({
            level,
            message,
            style,
        }) {
            if (style) {
                // eslint-disable-next-line
                console.log(`%c${level}: ${message}`, style);
            } else {
                // eslint-disable-next-line
                console.log(`${level}: ${message}`);
            }
        },

        /**
         * Sets current application step.
         */
        setCurrentStep(step) {
            this.currentStep = step;
        },

        /**
         * Sets whether the app is in mobile mode.
         */
        setIsMobile(isMobile) {
            this.isMobile = isMobile;
        },

        /**
         * Sets whether the app is in print mode.
         */
        setIsPrintMode(isPrintMode) {
            this.isPrintMode = isPrintMode;
        },

        setShouldShowResetModal(shouldShowResetModal) {
            this.shouldShowResetModal = shouldShowResetModal;
        },

        registerOpenModal() {
            this.openModalCount += 1;
        },

        unregisterOpenModal() {
            this.openModalCount = Math.max(0, this.openModalCount - 1);
        },

        setShouldShowSummary(shouldShowSummary) {
            this.shouldShowSummary = shouldShowSummary;
        },

        setShouldShowViewAll(shouldShowViewAll) {
            this.shouldShowViewAll = shouldShowViewAll;
        },

        openNestedPanel(panelId, payload = null) {
            if (!panelId) {
                return;
            }

            this.nestedPanels = [
                ...this.nestedPanels,
                {
                    panelId,
                    payload,
                },
            ];
        },

        closeNestedPanel() {
            if (!this.nestedPanels.length) {
                return;
            }

            this.nestedPanels = this.nestedPanels.slice(0, -1);
        },

        clearNestedPanels() {
            this.nestedPanels = [];
        },

        /**
         * Sets whether the product renderer is in fullscreen mode.
         */
        setIsFullscreen(isFullscreen) {
            this.isFullscreen = isFullscreen;
        },

        /**
         * Sets whether customizer is doing something that takes a long time.
         */
        setIsBusy(isBusy) {
            this.isBusy = isBusy;
        },

        /**
         * Sets whether customizer is loading an image.
         */
        setIsLoading(isBusy) {
            this.isLoading = isBusy;
        },

        /**
         * Sets whether the link to the design was copied to the clipboard.
         */
        setLinkCopied(linkCopied) {
            this.linkCopied = linkCopied;
        },

        /**
         * Close the widget.
         */
        globalDone() {
            this.selfDestruct = true;
        },

        /**
         * Close the widget without making changes.
         */
        globalCancel() {
            let destroy = true;

            if (this.options.onCancel) {
                if (this.options.onCancel()) {
                    this.isQuietBusy = true;

                    this.setIsBusy(true);

                    destroy = false;
                }
            }

            if (destroy) {
                this.selfDestruct = true;
            }
        },

        /**
         * Resets the app.
         */
        resetCustomization() {
            this.setCurrentStep(constants.STEP_RESET);

            setTimeout(() => {
                this.setCurrentStep(constants.STEP_LOADING);
            }, '1000');
        },

        /**
         * Sets whether the group overlay is shown.
         */
        setShowGroupOverlay(showGroupOverlay) {
            this.showGroupOverlay = showGroupOverlay;
        },

        showToast(toastInput = {}) {
            const toast = typeof toastInput === 'string'
                ? { message: toastInput }
                : toastInput;
            const message = `${toast?.message || ''}`.trim();

            if (!message) {
                return null;
            }

            const anchor = toast.anchor || 'top';
            const lastToast = [...this.toasts]
                .reverse()
                .find((existingToast) => (existingToast.anchor || 'top') === anchor);

            if (lastToast?.message === message) {
                return null;
            }

            const toastId = toast.id || `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const duration = Number.isFinite(toast.duration) ? toast.duration : 3000;
            const nextToast = {
                id: toastId,
                anchor,
                message,
                messageB: toast.messageB,
                dismissible: toast.dismissible !== false,
                icon: toast.icon,
                linkText: toast.linkText,
                linkAction: toast.linkAction,
                duration,
                hexColor: toast.hexColor,
            };

            this.toasts = [
                ...this.toasts,
                nextToast,
            ];

            if (duration > 0) {
                setTimeout(() => {
                    this.dismissToast(toastId);
                }, duration);
            }

            return toastId;
        },

        dismissToast(toastId) {
            if (!toastId) {
                return;
            }

            this.toasts = this.toasts.filter((toast) => toast.id !== toastId);
        },

        clearToasts(anchor = null) {
            if (!anchor) {
                this.toasts = [];
                return;
            }

            this.toasts = this.toasts.filter((toast) => (toast.anchor || 'top') !== anchor);
        },
    },
});
